import { useMemo } from "react";
import {
    DocumentCard,
    DocumentCardTitle,
    DocumentCardPreview,
    IDocumentCardPreviewImage,
    IDocumentCardPreviewStyles
} from "@fluentui/react/lib/DocumentCard";
import { Icon } from "@fluentui/react/lib/Icon";
import { Text } from "@fluentui/react/lib/Text";
import { Stack } from "@fluentui/react/lib/Stack";
import { TooltipHost } from "@fluentui/react/lib/Tooltip";
import { DirectionalHint } from "@fluentui/react";

import { AiOutlineMessage } from "react-icons/ai";
import { IoDocumentTextOutline } from "react-icons/io5";
import { BiSolidHide, BiShowAlt } from "react-icons/bi";
import { MdOutlineWorkspacePremium } from "react-icons/md";

import styles from "./GroupCardsList.module.css";
import frwrkStyles from "./FireWorks.module.css";

import { SettingsButton } from "../../components/SettingsButton";
import {
    assistantCodes,
    ImgMapKeys,
    imgMapSml,
    localStorageHiddenGroupsKey
} from "../../lsts";
import { ggroupcardprops } from "../../interfaces";
import { spaceTitleStyles, docCardStyles, spaceCardExtraInfoStyles, spaceCardOptionTextStyles, spaceCardDescriptionStyles, spaceCardGlobeIconTextStyles, spaceCardPremiumTextStyles, spaceCardEmtpyStyles } from "../../styles_glob";

// define the interface for props
interface DocumentCardComponentProps {
    index: number;
    item: ggroupcardprops;
    newSpacePyro: string;
    permissionLevelsMap: { [key: number]: string };
    setRefreshKey: React.Dispatch<React.SetStateAction<number>>;
    hidden: boolean;
    navChatLib: (loc: string, groupid: string) => void;
    rndr: boolean;
}

const noFadeStyle: Partial<IDocumentCardPreviewStyles> = {
    root: {
        selectors: {
            "& img": {
                opacity: "1 !important",
                transition: "none !important",
                animation: "none !important"
            },
            "& .ms-Image": {
                opacity: "1 !important",
                transition: "none !important",
                animation: "none !important"
            }
        },
        width: "100%",
        margin: "0px",
        padding: "0px",
        objectFit: "cover",
        borderRadius: "10px 10px 0px 0px"
    }
};
const tooltipStyles = {};
const DocumentCardComponent: React.FC<DocumentCardComponentProps> = ({
    index,
    item,
    newSpacePyro,
    permissionLevelsMap,
    setRefreshKey,
    hidden,
    navChatLib,
    rndr
}) => {
    const imgSrc = useMemo(
        () => ({
            previewImages: [
                {
                    name: "Assistant",
                    previewImageSrc: item?.selectedGroup?.headerimageurl || imgMapSml[(item?.selectedGroup?.assistanttype ?? "anya") as ImgMapKeys],
                    imageFit: 2,
                    width: 318,
                    height: 120
                } as IDocumentCardPreviewImage
            ],
            loadingMode: "none"
        }),
        [rndr]
    );

    return (
        <DocumentCard
            key={index}
            id={`${item.selectedGroup.oid}`}
            styles={docCardStyles}
            onClick={() => {item.selectedGroup.hideroottabs != 1 && item.selectedGroup.assistanttype != assistantCodes.ipa && item.selectedGroup.landonmanage != 1 ? item.handleGroupListClick(item.selectedGroup.selectionId, item.selectedGroup.selectionText, item.selectedGroup.permissionlevel) : navChatLib("lib", item.selectedGroup.selectionId)}}
        >
            <DocumentCardPreview {...imgSrc} className={styles.assistantImage} styles={noFadeStyle} />
            <Stack tokens={{ childrenGap: 10 }} className={styles.allCardText}>
                {/* space title */}
                <DocumentCardTitle
                    title={item.selectedGroup.selectionText}
                    styles={spaceTitleStyles}
                />
                {newSpacePyro === item.selectedGroup.selectionId && (
                    <div className={frwrkStyles.fireworksContainer}>
                        <div className={frwrkStyles.pyro}>
                            <div className={frwrkStyles.before}></div>
                            <div className={frwrkStyles.after}></div>
                        </div>
                    </div>
                )}
                {hidden == false ? (
                    <div className={styles.spaceCardExtraInfo}>
                        <Text
                            variant="medium"
                            styles={spaceCardExtraInfoStyles}
                        >
                            <TooltipHost content="Number of Documents in Space" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                                <div className={styles.flCGr}>
                                    <div style={{ marginLeft: "-4px", marginRight: "5px", height: "20px", alignItems: "center", display: "flex"}}>
                                        <IoDocumentTextOutline size={15} />
                                        <div style={{ fontSize: "13px", marginLeft: "3px" }}>{item.selectedGroup.filecount}</div>
                                    </div>
                                </div>
                            </TooltipHost>
                            {item.selectedGroup.permissionlevel > 2 && (
                                <TooltipHost
                                    content="Message exchanges done today"
                                    directionalHint={DirectionalHint.bottomLeftEdge}
                                    calloutProps={tooltipStyles}
                                >
                                    ·
                                    <div className={styles.flCGr}>
                                        <div style={{ marginTop: "-14px", marginLeft: "1px", marginRight: "5px", height: "10px", alignItems: "center", display: "flex"}}>
                                            <div style={{ marginBottom: "-5px", marginLeft: "5px", marginRight: "3px"}}>
                                                <AiOutlineMessage size={15} />
                                            </div>{" "}
                                            <div style={{ fontSize: "13px", marginLeft: "3px" }}>{item.selectedGroup.dailymsgs} </div>
                                        </div>
                                    </div>
                                </TooltipHost>
                            )}
                            <div className={styles.spaceBeforeOwner}>· </div>
                            <TooltipHost content="Your Permission Level" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                                <div className={styles.permissionLevelText}> {permissionLevelsMap[item.selectedGroup.permissionlevel]}</div>
                            </TooltipHost>
                        </Text>
                        <Text
                            variant="medium"
                            styles={spaceCardEmtpyStyles}
                        ></Text>
                        {/*LICENSE PREMIUM ICON*/}
                        <div className={styles.spaceDetailsIcons}>
                            {item.selectedGroup.premiumby !== "" && (
                                <Text
                                    variant="medium"
                                    title="Premium Space"
                                    styles={spaceCardPremiumTextStyles}
                                >
                                    <MdOutlineWorkspacePremium />
                                </Text>
                            )}

                            {item.selectedGroup.inscope === 0 && (
                                <Text
                                    variant="medium"
                                    title="General Chat (allow answers to anything)"
                                    styles={spaceCardGlobeIconTextStyles}
                                >
                                    <Icon iconName="Globe" />
                                </Text>
                            )}
                        </div>

                        <Text
                            variant="medium"
                            styles={spaceCardDescriptionStyles}
                        >
                            {item.selectedGroup.description}
                        </Text>
                    </div>
                ) : (
                    <div className={styles.spaceCardExtraInfo}> </div>
                )}

                <Text
                    variant="medium"
                    styles={spaceCardOptionTextStyles}
                >
                    <div className={styles.greyLine} />
                    {hidden == true ? (
                        <div
                            className={styles.hideSpaceIcon}
                            onClick={(ev: React.MouseEvent) => {
                                ev.preventDefault();
                                ev.stopPropagation();
                                localStorage.setItem(localStorageHiddenGroupsKey, (localStorage.getItem(localStorageHiddenGroupsKey) || "").replace(item.selectedGroup.selectionId, ""));
                                setRefreshKey(oldKey => oldKey + 1);
                            }}
                        >
                            <TooltipHost
                                content="Return Assistant to your main 'All' Section"
                                directionalHint={DirectionalHint.bottomLeftEdge}
                                calloutProps={tooltipStyles}
                            >
                                <BiShowAlt />
                            </TooltipHost>
                        </div>
                    ) : (
                        <div
                            className={styles.hideSpaceIcon}
                            onClick={(ev: React.MouseEvent) => {
                                ev.preventDefault();
                                ev.stopPropagation();
                                localStorage.setItem(localStorageHiddenGroupsKey, `${localStorage.getItem(localStorageHiddenGroupsKey) || ""},${item.selectedGroup.selectionId}`);
                                setRefreshKey(oldKey => oldKey + 1);
                            }}
                        >
                            <TooltipHost
                                content="Hide Assistant. You will be able to find it in the 'Hidden' Section"
                                directionalHint={DirectionalHint.bottomLeftEdge}
                                calloutProps={tooltipStyles}
                            >
                                <BiSolidHide size={17} />
                            </TooltipHost>
                        </div>
                    )}
                    {hidden == false && (
                        <div className={styles.headSpaceButts}>
                            {item.selectedGroup.hideroottabs != 1 && (
                                <TooltipHost content="Chat with this Assistant" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                                    <SettingsButton
                                        className={`${styles.settingsButton}`}
                                        onClick={() => {
                                            //navChatLib("chat", item.selectedGroup.selectionId);
                                            item.handleGroupListClick(
                                                item.selectedGroup.selectionId,
                                                item.selectedGroup.selectionText,
                                                item.selectedGroup.permissionlevel
                                            );
                                        }}
                                        buttonText="Chat"
                                        iconId="PiChatTeardropDotsBold"
                                        forceIcon={true}
                                    />
                                </TooltipHost>
                            )}
                            <TooltipHost
                                content="View Content for this Assistant"
                                directionalHint={DirectionalHint.bottomLeftEdge}
                                calloutProps={tooltipStyles}
                            >
                                <SettingsButton
                                    className={`${styles.settingsButton}`}
                                    onClick={() => {
                                        navChatLib("lib", item.selectedGroup.selectionId);
                                    }}
                                    buttonText="Manage"
                                    iconId="TbSettings2"
                                    forceIcon={true}
                                />
                            </TooltipHost>
                        </div>
                    )}
                </Text>
            </Stack>
        </DocumentCard>
    );
};

export default DocumentCardComponent;
